import type { UserRole } from "./api";

export type AuthTokens = {
  accessToken: string;
  refreshToken: string;
  role: UserRole;
  accessExpiresAt: number;
  idleExpiresAt: number | null;
  /** Admin idle lock (minutes). Null for students and legacy sessions. */
  idleLockMinutes?: number | null;
  isSuperAdmin?: boolean;
  permissions?: string[];
};

export function mapTokenResponse(data: {
  access_token: string;
  refresh_token: string;
  role: UserRole;
  access_expires_at: number;
  idle_expires_at?: number | null;
  idle_lock_minutes?: number | null;
  is_super_admin?: boolean;
  permissions?: string[];
}): AuthTokens {
  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    role: data.role,
    accessExpiresAt: data.access_expires_at,
    idleExpiresAt: data.idle_expires_at ?? null,
    idleLockMinutes: data.idle_lock_minutes ?? null,
    isSuperAdmin: data.is_super_admin ?? false,
    permissions: data.permissions ?? [],
  };
}

export function isAccessExpired(tokens: AuthTokens, skewSeconds = 60): boolean {
  return Date.now() / 1000 >= tokens.accessExpiresAt - skewSeconds;
}

export function isIdleExpired(tokens: AuthTokens): boolean {
  if (tokens.role !== "admin" || !tokens.idleExpiresAt) return false;
  return Date.now() / 1000 >= tokens.idleExpiresAt;
}

export function canAccess(tokens: AuthTokens | null, feature: string): boolean {
  if (!tokens || tokens.role !== "admin") return false;
  if (tokens.isSuperAdmin) return true;
  return (tokens.permissions ?? []).includes(feature);
}

export function canAccessAny(tokens: AuthTokens | null, features: string[]): boolean {
  return features.some((f) => canAccess(tokens, f));
}
